import { useEffect, useRef, useState } from 'react';
import { chatAPI } from '../utils/api';

/**
 * useUnreadChatCount — polls /api/conversations/unread-count every 12s while authed.
 * Returns { unreadConvos, unreadMessages, refresh }.
 *
 * Designed to be cheap: lightweight aggregation on the server, no payload growth.
 */
export default function useUnreadChatCount(user, { intervalMs = 12000 } = {}) {
  const [counts, setCounts] = useState({ unreadConvos: 0, unreadMessages: 0 });
  const intervalRef = useRef(null);

  const fetchOnce = async () => {
    try {
      const res = await chatAPI.getUnreadCount();
      setCounts({
        unreadConvos: res.data?.unread_conversations || 0,
        unreadMessages: res.data?.unread_messages || 0,
      });
    } catch (_e) { /* silently ignore network blips */ }
  };

  useEffect(() => {
    if (!user) {
      setCounts({ unreadConvos: 0, unreadMessages: 0 });
      return;
    }
    fetchOnce();
    intervalRef.current = setInterval(fetchOnce, intervalMs);
    // Also refresh when tab becomes visible again
    const onVis = () => { if (document.visibilityState === 'visible') fetchOnce(); };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      document.removeEventListener('visibilitychange', onVis);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.user_id]);

  return { ...counts, refresh: fetchOnce };
}
